import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import questionsData from '../data/json/combined_fin_vocab.json';
import { getCategoryIcon } from '../utils/iconUtils';
import { getReviewStats } from '../utils/reviewUtils';
import { FaRedo, FaExclamationTriangle } from 'react-icons/fa';

const HomePage = () => {
    const [reviewStats, setReviewStats] = useState({ totalCount: 0 });

    useEffect(() => {
        const stats = getReviewStats();
        setReviewStats(stats);
    }, []);

    // カテゴリごとにグループ化
    const categories = [...new Set(questionsData.filter(item => item.category).map(item => item.category))];
    
    // 品詞と難易度ごとにグループ化
    const partOfSpeechAndDifficulty = [...new Set(questionsData.filter(item => !item.category).map(item => `${item.part_of_speech}-${item.difficulty}`))];
    
    // カテゴリ内の単語数を計算
    const getCategoryWordCount = (category) => {
        return questionsData.filter(item => item.category === category).length;
    };

    // 品詞と難易度内の単語数を計算
    const getPartOfSpeechAndDifficultyWordCount = (part_of_speech, difficulty) => {
        return questionsData.filter(item => item.part_of_speech === part_of_speech && item.difficulty === difficulty).length;
    };

    // const getPartOfSpeechCount = (part_of_speech, difficulty) => {
    //     return questionsData.filter(item => item.part_of_speech === part_of_speech).length;
    // };


    return (
        <div className="modern-container">
            {/* Hero Section */}
            <div className="modern-section text-center">
                <div className="fade-in-up">
                    <h1 style={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        marginBottom: 'var(--spacing-lg)'
                    }}>
                        フィンランド語単語クイズ
                    </h1>
                    <p style={{ 
                        fontSize: '1.25rem', 
                        color: 'var(--text-secondary)',
                        maxWidth: '600px',
                        margin: '0 auto var(--spacing-xl) auto'
                    }}>
                        楽しく効果的にフィンランド語の語彙を学習しましょう
                    </p>
                </div>
            </div>
            
            {/* 復習機能 */}
            {reviewStats.totalCount > 0 && (
                <div className="modern-alert modern-alert-warning fade-in-up" style={{ marginBottom: 'var(--spacing-2xl)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                            <FaExclamationTriangle size={20} />
                            <div>
                                <strong>復習が必要な問題があります！</strong>
                                <span className="modern-badge modern-badge-danger" style={{ marginLeft: 'var(--spacing-md)' }}>
                                    {reviewStats.totalCount}問
                                </span>
                            </div>
                        </div>
                        <Link to="/review" className="modern-btn modern-btn-warning">
                            <FaRedo />
                            復習を始める
                        </Link>
                    </div>
                </div>
            )}
            
            {/* カテゴリー別クイズ */}
            <div className="modern-section">
                <h2 style={{ 
                    textAlign: 'center', 
                    marginBottom: 'var(--spacing-2xl)',
                    color: 'var(--text-primary)'
                }}>
                    カテゴリー別クイズ
                </h2>
                <Row>
                    {categories.map((category, index) => (
                        <Col key={index} xs={12} sm={6} md={4} className="mb-4">
                            <div className="modern-card fade-in-up" style={{ 
                                animationDelay: `${index * 0.1}s`,
                                height: '100%'
                            }}>
                                <div className="modern-card-body" style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    height: '100%'
                                }}>
                                    <div style={{ 
                                        fontSize: '2rem',
                                        textAlign: 'center',
                                        marginBottom: 'var(--spacing-md)'
                                    }}>
                                        {getCategoryIcon(category)}
                                    </div>
                                    <h4 style={{ 
                                        textAlign: 'center',
                                        marginBottom: 'var(--spacing-md)',
                                        color: 'var(--text-primary)'
                                    }}>
                                        {category}
                                    </h4>
                                    <p style={{ 
                                        textAlign: 'center',
                                        color: 'var(--text-muted)',
                                        marginBottom: 'var(--spacing-lg)',
                                        flex: 1
                                    }}>
                                        {getCategoryWordCount(category)} words
                                    </p>
                                    <Link 
                                        to={`/quiz-list/${category}`} 
                                        className="modern-btn modern-btn-primary"
                                        style={{ width: '100%', justifyContent: 'center' }}
                                    >
                                        {category}クイズ一覧へ
                                    </Link>
                                </div>
                            </div>
                        </Col>
                    ))}
                </Row>
            </div>
            
            {/* 品詞別クイズ */}
            <div className="modern-section">
                <h2 style={{ 
                    textAlign: 'center', 
                    marginBottom: 'var(--spacing-2xl)',
                    color: 'var(--text-primary)'
                }}>
                    品詞別クイズ
                </h2>
                <Row>
                    {partOfSpeechAndDifficulty.map((item, index) => {
                        const [part_of_speech, difficulty] = item.split('-');
                        const difficultyColor = difficulty === '初級' ? 'success' : 
                                              difficulty === '中級' ? 'warning' : 'danger';
                        return (
                            <Col key={index} xs={12} sm={6} md={4} className="mb-4">
                                <div className="modern-card fade-in-up" style={{ 
                                    animationDelay: `${(index + categories.length) * 0.1}s`,
                                    height: '100%'
                                }}>
                                    <div className="modern-card-body" style={{ 
                                        display: 'flex', 
                                        flexDirection: 'column',
                                        height: '100%'
                                    }}>
                                        <div style={{ 
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: 'var(--spacing-md)'
                                        }}>
                                            <h4 style={{ 
                                                color: 'var(--text-primary)',
                                                margin: 0
                                            }}>
                                                {part_of_speech}
                                            </h4>
                                            <span className={`modern-badge modern-badge-${difficultyColor}`}>
                                                {difficulty}
                                            </span>
                                        </div>
                                        <p style={{ 
                                            color: 'var(--text-muted)',
                                            marginBottom: 'var(--spacing-lg)',
                                            flex: 1
                                        }}>
                                            {getPartOfSpeechAndDifficultyWordCount(part_of_speech, difficulty)} words
                                        </p>
                                        <Link 
                                            to={`/quiz-list/${part_of_speech}/${difficulty}`} 
                                            className="modern-btn modern-btn-secondary"
                                            style={{ width: '100%', justifyContent: 'center' }}
                                        >
                                            クイズを開始
                                        </Link>
                                    </div>
                                </div>
                            </Col>
                        );
                    })}
                </Row>
            </div>
        </div>
    );
};

export default HomePage;
